// IBM Cognos to Amazon Quick page content. Same shape as domo.js; rendered
// by PlatformMigrationContent (components/BiPlatformPage).
export const ibmCognosPage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from IBM Cognos to Amazon Quick',
    intro: 'The reasons we hear most from Cognos customers running on AWS.',
    cards: [
      { title: 'A lighter platform to run', description: 'No dispatchers, gateways, Content Store, or version upgrades to manage. Quick is fully managed on AWS.' },
      { title: 'Paginated and interactive in one place', description: 'Operational reports and interactive dashboards run side by side on Quick, on the same data.' },
      { title: 'AI agents on the same data', description: 'Once reports are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Spend that counts on AWS', description: 'Quick and the AWS services under it can draw down your existing AWS commitment.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your Cognos metadata',
    title: 'We read your Cognos environment through its APIs and models',
    intro: 'Instead of weeks of interviews, we pull metadata straight from Cognos. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'Packages, query subjects, and query items', from: 'Framework Manager models', use: 'Map the model to Quick datasets and size the data landing in Amazon Redshift' },
      { what: 'Data modules and calculations', from: 'Cognos Analytics REST API', use: 'Convert calculations to Quick calculated fields' },
      { what: 'Reports, prompts, and report specifications', from: 'Content Store and report specs (XML)', use: 'Rebuild as paginated reports, prompts as Quick controls, and spot duplicates' },
      { what: 'Dashboards and stories', from: 'Cognos Analytics REST API', use: 'Rebuild as interactive Quick Sight dashboards' },
      { what: 'PowerCubes and dynamic cubes', from: 'Transformer models and cube definitions', use: 'Rebuild in Amazon Redshift or SPICE' },
      { what: 'Report runs and logons', from: 'Audit database', use: 'Find the most-used and unused reports to rank waves and retire dead content' },
      { what: 'Namespaces, groups, schedules, and bursting', from: 'Content Store and REST API', use: 'Recreate row-level security, map to SSO, and move schedules to scheduled reports' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every report traced back through its package and query subjects to the source tables.' },
        { title: 'Complexity score per report', description: 'Prompts, calculations, and security scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by the audit database, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Cognos and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Cognos admin sets up a read-only service account and read access to the audit database. Nothing in Cognos changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Cognos migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every report.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled from Cognos: packages, data modules, report specs, cubes, security, and audit usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Reports grouped into waves by usage, shared packages, and folder. Unused reports are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'Packages mapped to datasets, calculations converted to calculated fields, and reports rebuilt as paginated reports or dashboards.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated report compared with its Cognos original. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who runs which Cognos reports today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than reports: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Cognos reports used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
